import React, { useCallback } from "react";
import { useUpdateEffect } from "react-use";
import { Trigger } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import useDrivePicker from "~/components/Google/useDrivePicker";
import i18n from "~/i18n";
import Angie from "~/utils/angie";

/* global google */

const dispatchEvent = (val) => {
  const event = new CustomEvent("google-drive-picker", {
    detail: val,
  });
  document.dispatchEvent(event);
};

const translations = {
  access_denied: i18n.t("Authentication failed!"),
};

export const GooglePicker = ({ clientId, className, label, onGoogleAuthError, onChange, apiKey }) => {
  const [openPicker, authResponse, loaded] = useDrivePicker();

  const pickerCallback = useCallback(
    (data) => {
      if (data.action === google.picker.Action.PICKED) {
        const documents = data[google.picker.Response.DOCUMENTS].map((doc) => {
          return {
            ...doc,
            code: Angie.functions.random_hash(),
          };
        });

        onChange(documents);
      } else if (data.action === google.picker.Action.CANCEL) {
        dispatchEvent(false);
      }
    },
    [onChange]
  );

  const errorCallback = useCallback(
    (response) => {
      if (translations[response.error]) {
        onGoogleAuthError(translations[response.error]);
      } else {
        onGoogleAuthError(response.error);
      }
    },
    [onGoogleAuthError]
  );

  const handleAuthClick = useCallback(() => {
    // const view = new google.picker.View(google.picker.ViewId.DOCS);
    openPicker({
      clientId: clientId,
      developerKey: apiKey,
      // viewId: view,
      multiselect: false,
      showUploadView: false,
      showUploadFolders: false,
      callbackFunction: pickerCallback,
      errorHandler: errorCallback,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, openPicker, pickerCallback, errorCallback]);

  useUpdateEffect(() => {
    if (authResponse && authResponse.access_token) {
      dispatchEvent(true);
    }
  }, [authResponse]);

  return (
    <Trigger
      onClick={handleAuthClick}
      disabled={loaded === false}
      className={classNames(
        `google_picker_button tw-leading-7`,
        {
          "tw-opacity-50": !loaded,
        },
        className
      )}
    >
      {label}
    </Trigger>
  );
};

GooglePicker.propTypes = {
  clientId: PropTypes.string.isRequired,
  appId: PropTypes.string.isRequired,
  apiKey: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onGoogleAuthError: PropTypes.func.isRequired,
  className: PropTypes.string,
};

GooglePicker.defaultProps = {
  label: i18n.t("Google Drive"),
  onChange: () => null,
};
